import {IRunningGameHandler} from '../games/running-game-handler.interface';
import {IAutoLoginParams} from '../../../common/interfaces/autologin-params.interface';
import IStructuredLoggingService from "../../../common/interfaces/structured-logging-service-interface";
import {IGeoLocationInfo} from "../../../common/interfaces/geo-location-info.interface";
import {GamingHistoryRecordRawData} from "../gaming-history/gaming-history.service.interfce";
import {ISdkHybridService} from "../hybrid/hybrid.service.interface";
import {ILegacyRegulation} from "../regulation/legacy-regulation.interface";
import {NullableNumber, NullableString} from "../../../common/types/nullable-types";
import {IScriptLoaderService} from "../scrip-loader/script-loader.service.interface";
import IStateOfResidence from "../../../common/interfaces/state-of-residence.interface";
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {GNWP_SOURCES} from "../gnwp/gnwp-consts";
import {LoadScriptOptions} from "../../../common/interfaces/load-script-options";
import {IInactivityTimeoutSettings} from "../user/model/inactivity-timeout-settings";
import {TimeSpan} from "@sparkware/app-utils";
import {GameRawData} from "./gme-raw-data.type";


export interface ITokenInfo {
  readonly loginIP: string;
}

export interface CmsUserSignature {
  readonly message: string;
  readonly signature: string;
}

interface OnEventParams {
  eventName: string;
  callback: Function;
  context: any;
}

interface SendAnalyticsParams {
  category: string;
  action: string;
  label: string;
  value: string;
  omitStructuredLogger?: boolean
}





export const CGP_EVENTS_NAMES = {
  ON_BEFORE_OAUTH_STATE_CHANGE: 'ON_BEFORE_OAUTH_STATE_CHANGE',
  OAUTH_STATE_CHANGE: 'OAUTH_STATE_CHANGE',
  RECENTLY_PLAYED_GAMES_UPDATED: 'RECENTLY_PLAYED_GAMES_UPDATED',
  AUTOLOGIN_SUCCESS: 'AUTOLOGIN_SUCCESS',
  AUTOLOGIN_FAILED: 'AUTOLOGIN_FAILED',
  DEEPLINK_RECEIVED: 'DEEPLINK_RECEIVED',
  GAME_OPEN: 'GAME_OPEN',
  CLOSE_GAME: 'CLOSE_GAME',
  LOGIN_FORM_OPEN: 'LOGIN_FORM_OPEN',
  LOGIN_FORM_CLOSE: 'LOGIN_FORM_CLOSE',
  TOKEN_LIFETIME_EXPIRED: 'TOKEN_LIFETIME_EXPIRED',
  LIVE_TABLES_UPDATE: 'LIVE_TABLES_UPDATE',
  GAME_POPUP_SHOW: 'GAME_POPUP_SHOW',
  REGISTRATION_COMPLETED: 'REGISTRATION_COMPLETED',
  BEGIN_LOGIN_AFTER_REGISTRATION: 'BEGIN_LOGIN_AFTER_REGISTRATION',
  LOGIN_COMPLETED_AFTER_REGISTRATION: 'LOGIN_COMPLETED_AFTER_REGISTRATION',
  LOGIN_FAILED_AFTER_REGISTRATION: 'LOGIN_FAILED_AFTER_REGISTRATION',
  APP_RESUMED: 'APP_RESUMED',
  APP_PAUSED: 'APP_PAUSED',
  EXTERNAL_LINK_CLOSED: 'EXTERNAL_LINK_CLOSED',
  TOUCH_ID_STATE_CHANGED: 'TOUCH_ID_STATE_CHANGED',
  OMG_DISPLAYED: 'OMG_DISPLAYED',
  LIVE_GAME_SWITCHED: 'LIVE_GAME_SWITCHED',
  DEPOSIT_WIDGET_CLOSED: 'DEPOSIT_WIDGET_CLOSED',
};


export interface DeviceInfo {
  os: {
    name: string;
    version: number;
    isMobileOS: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    isPC: boolean;
    isMacOS: boolean;
  };
  browser: {
    name: string;
    version: number;
    isSafari: boolean;
    isChrome: boolean;
    isFirefox: boolean;
  };
  model: {
    isIphone: boolean;
    isIpad: boolean;
    deviceType: string | undefined;
  };
  hybrid: {
    isHybrid: boolean;
    isEmbedded: boolean;
    inCasino: boolean;
    inPoker: boolean;
    inSport: boolean;
  };
  feature: {
    isTouch: boolean;
  };
  viewport: {
    width: number;
    height: number;
    screenWidth: number;
    screenHeight: number;
    pixelRatio: number;
  };
  isProgressiveWebApp: boolean;
  isStandalone: boolean;

  /** @deprecated */
  isMobile(): boolean;
  /** @deprecated */
  isTablet(): boolean;

}

export interface CGPEventSubscription {
  off();
}


export interface GetAutoLoginTokenResponse {
  token: string;
}


export interface IDailyDealCampaign {
  MarketingCampaignId: string;
}

export interface IAppFlayerData {
  appsflyerid: string,
  appname: string,
  deviceid: string,
  afinstall: string,
}

export interface ISkinSerialData extends IAppFlayerData {
  [key: string]: any;
}

export interface CgpSkinInfo {
  getBrandName(): string;
  getPlatformSystemID(): number;
  getClientPlatform(): string;
  getVersion(): string;
  getCulture(): string;
  getMinimumAge(): number;
  getSerialData(marketingData: any): Promise<ISkinSerialData>;
  getDefaultHybridSerial(): string;
}

export interface ISdkRegulationService {
  isUSRegulation(): boolean;
  getRegulationCode(): number;
  shouldShowPlayerLimitsToaster(): boolean;
  getErrorMessage(errorCode: number): NullableString;
  isLoginPopupEnabled(): boolean;
}

export interface IOpenHistoryGameRequest {
  historyRecordData: GamingHistoryRecordRawData;
  gameType: number,
  gameName: string,
  gameRoundId: string,
  gameRoundDate: string;
}


export interface ICgpSdk {
  countPlayerChallenges(): number;
  device(): DeviceInfo;
  displayHybrid?: () => void;
  isHybridEmbeddedGame(gameType: number): boolean;
  openHybridLink(url: string): void;
  format$$$(value: number, withDecimal: boolean): string;
  getCurrencyDecimalSymbol(): string;
  getAutoLoginToken(): Promise<GetAutoLoginTokenResponse>;
  getBrandID(): number;
  getExternalBrandCallbackURL(): string;
  getExternalBrandCallbackURLMethod(): string;
  getGamesInfo(gameTypes: number[]): Promise<GameRawData>;
  getGamesThemes(): Record<number, string>;
  getGamesVendors(): Record<number, string>;
  getGamesCompanies(): Record<number, string>;
  getGamesInfoForHistory(gameTypes: number[]): Promise<GameRawData>;
  getGRSEnvironments(): Promise<any>;
  getSourceSubBrandName(): string;
  getSubBrandID(): number;
  getUserData(): any;
  getSessionId(): NullableNumber;
  getInitialSessionId(): NullableNumber;
  getUserSegments(): CmsUserSignature;
  isAuthorized(): boolean;
  isLoginFromRegistration(): boolean;
  structuredLoggingService(): IStructuredLoggingService;
  isVIP(): boolean;
  logout(): Promise<void>;
  menuActionExecute(key: string): void;
  onEvent(params: OnEventParams): CGPEventSubscription;
  routeChanged(): void;
  openGame(options: any): Promise<IRunningGameHandler>;
  openLoginForm(marketingParams?: MarketingParams): Promise<void>;
  sendAnalytics(params: SendAnalyticsParams): void;
  setAutoLoginToken(autologinToken: any): void;
  startLiveTableFeeds(): void;
  stopLiveTableFeeds(): void;


  getProductPackage(): number;

  getSkin(): CgpSkinInfo;
  runAutoLogin(autoLoginParams: IAutoLoginParams): Promise<void>;


  onOrbitInitComplete(): void;

  getEnvironment(): string;
  pushDataToDataLayer(data: {}): void;
  getCountryName(): string;
  getCountryIso3(): string;
  getSupportedStates(): IStateOfResidence[];
  getWrongLocationDetectionPath(): string;

  getRegulationService(): ISdkRegulationService;

  waitForAutologin(): Promise<void>;
  httpPost<TResponse>(url: string, requestData: unknown): Promise<TResponse>;
  httpGet<TResponse>(url: string, requestData: unknown): Promise<TResponse>;
  getConfigs(): any;
  loadScript(options: LoadScriptOptions): Promise<void>;
  getRefreshToken(): string;
  getCurrencyIso3(): string;
  getGoogleClientID(): string;
  getGoogleSessionID(): string;
  isInAutologin(): boolean;
  fireDepositWidgetClosed(): void;
  fireFreeToPlayWidgetClosed(): void;
  getToken(): ITokenInfo | null;
  isDepositWidgetSupportedByHybrid(): boolean;
  getGeoLocationInfo(): Promise<IGeoLocationInfo | null>;
  getDownloadAppLink(): string;
  promptPushNotificationPermissionRequest(): void;
  openPopUpOnSecondWebView(content: string, timeout: number): void;
  closePopUpOnSecondWebView(): void;
  readonly hybrid: ISdkHybridService;
  readonly currentRegulation: ILegacyRegulation;
  getCurrentBrowserTabId(): string;
  readonly scriptLoader: IScriptLoaderService;
  startAutoRetryGeoProcess(): Promise<void>;
  extractTokenProperty(tokenProperty: string): NullableString;
  updateTokenProperty(tokenProperty: string, value: NullableString): void;
  isLoginPopupEnabled(): boolean;
  getAccountData(): any;
  getTempAccountGnwpSource(): GNWP_SOURCES;
  getInactivityTimeoutSettings(): IInactivityTimeoutSettings;
  setInactivityTimeout(timeout: TimeSpan): void;
  createUserActivityDetactor(params): void;
  writeWidgetLogs(payload: any): Promise<void>;
  getFakeClientIP(): NullableString;
}
